/**
 * Version watcher — makes long-lived tabs pick up new deploys by themselves.
 *
 * Every deploy writes a unique build ID to /version.json (scripts/stamp-build.js).
 * On app start we remember the ID the page was served with; whenever the user
 * returns to the tab (and on a slow interval) we re-fetch version.json and, if
 * the ID changed, reload once to get the current build.
 *
 * Why "on focus": the classic stale-page report is a tab left open for days —
 * nothing ever re-fetches index.html, so fixes never arrive until a manual
 * hard refresh. Checking at the moment the user returns means they always
 * resume on the newest build, and reloading right at focus (before they start
 * typing) can't destroy in-progress form input.
 *
 * Loop guard: at most one auto-reload per detected build ID per tab session,
 * so a misbehaving version.json can never reload-loop the browser.
 */

const CHECK_INTERVAL_MS = 10 * 60 * 1000;
const GUARD_KEY = 'version-watch-reloaded-for';

let baselineBuild = null;
let checking = false;

const fetchBuildId = async () => {
  const res = await fetch('/version.json', { cache: 'no-store' });
  if (!res.ok) return null;
  const data = await res.json();
  return data && data.build ? String(data.build) : null;
};

const checkForNewBuild = async () => {
  if (checking) return;
  checking = true;
  try {
    const current = await fetchBuildId();
    if (!current) return;
    if (baselineBuild === null) {
      baselineBuild = current;
      return;
    }
    if (current !== baselineBuild) {
      let guarded = null;
      try { guarded = sessionStorage.getItem(GUARD_KEY); } catch (e) { /* private mode */ }
      if (guarded === current) return;
      try { sessionStorage.setItem(GUARD_KEY, current); } catch (e) { /* private mode */ }
      window.location.reload();
    }
  } catch (e) {
    // Network hiccup — try again next focus/interval.
  } finally {
    checking = false;
  }
};

export const installVersionWatcher = () => {
  checkForNewBuild();
  window.addEventListener('focus', checkForNewBuild);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') checkForNewBuild();
  });
  setInterval(checkForNewBuild, CHECK_INTERVAL_MS);
};
